import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import * as Icons from "lucide-react";
import { ArrowRight, ClipboardList, PencilLine, MonitorPlay, Send, Check, MessageCircle,
  HelpCircle } from "lucide-react";
import { PageHero } from "@/components/layout/PageHero";
import { BrandDot } from "@/components/util/BrandDot";
import { Reveal } from "@/components/util/Reveal";
import { usePageSeo } from "@/lib/usePageSeo";
import { PUBLIC_DISCOVERY_GROUPS as DISCOVERY_GROUPS } from "@/lib/discoveryGroups";

// Phase 2 CMS Reconciliation (Feb 2026) — /services finder tiles now
// come from `services[].finder_items[]`. See ServicesSection.jsx for
// the parallel Home surface. No local FINDER_TILES array anymore.
const flattenServicesFinderItems = (services) => {
  const rows = [];
  (services || []).forEach((svc) => {
    (svc.finder_items || []).forEach((item) => {
      if (!item?.show_on_services) return;
      rows.push({
        key: item.key || `${svc.slug}-${item.label}`,
        label: item.label || "",
        icon: item.icon_key ? `/finder-icons/${item.icon_key}.png` : "",
        to: item.href || `/services/${svc.slug}${item.focus_param ? `?focus=${item.focus_param}` : ""}`,
        groups: Array.isArray(item.discovery_groups) ? item.discovery_groups : [],
        order: Number.isFinite(item.order) ? item.order : 0,
      });
    });
  });
  rows.sort((a, b) => a.order - b.order);
  return rows;
};

// Desktop layout override — first 6 tiles form the top row, remaining
// form the bottom row. Both rows render independently at the xl
// breakpoint and each is horizontally centered on its own axis. At
// smaller breakpoints the ordering is preserved through the grid
// (mobile/tablet render the full list in normal reading order).
const FINDER_ROW_SPLIT = 6;

const PROCESS_FALLBACK = [
  { icon: "ClipboardList", title: "Discover", sub: "We learn about your business, goals and challenges." },
  { icon: "PencilLine", title: "Strategize", sub: "We plan the right solution tailored to your brand." },
  { icon: "MonitorPlay", title: "Design & Create", sub: "Our team brings ideas to life with precision and creativity." },
  { icon: "Send", title: "Review & Launch", sub: "You review, we refine, and your brand is ready to grow." },
];

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const s = useSettings();
  const location = useLocation();
  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
  }, []);

  // #services-finder deep links are positioned by the site-wide
  // ScrollToTop manager (single scroll owner).

  // Contextual-mode gate. Set only when the URL literally contains
  // `?for=existing-brand`. Every other visit — plain /services, nav
  // clicks, deep links, refresh with no query — renders the default
  // page byte-identical to before this file's contextual branch
  // was added.
  const forParam = new URLSearchParams(location.search).get("for");
  // Six-category discovery filter (?group=<key>) — shared with the
  // homepage group cards. Unknown/absent → show everything.
  const groupParam = new URLSearchParams(location.search).get("group") || "";
  const activeGroup = DISCOVERY_GROUPS.some((g) => g.key === groupParam) ? groupParam : "";
  const contextExistingBrand = forParam === "existing-brand";

  usePageSeo({
    title: "Design Services — Ur Designs",
    description: "Ur Designs creative services — branding, print & signage, digital & ads, packaging, and 3D & motion. Fixed-price packages and custom estimates.",
    path: "/services",
  });

  const serviceInGroup = (svc) =>
    !activeGroup ||
    (svc.finder_items || []).some((f) => (f.discovery_groups || []).includes(activeGroup));

  // Phase 2 CMS Reconciliation — flatten finder tiles from CMS.
  const finderTiles = flattenServicesFinderItems(services).filter(
    (t) => !activeGroup || t.groups.includes(activeGroup)
  );

  const eyebrow = s.services_eyebrow;
  const heading1 = s.services_heading_line1;
  const heading2 = s.services_heading_line2;
  const heroDesc = s.services_hero_description;
  const heroDescMobile = s.services_hero_description_mobile || "";
  const scopeNote = s.services_scope_note || "";
  const processEyebrow = s.services_process_eyebrow;
  const processH1 = s.services_process_heading_line1;
  const processH2 = s.services_process_heading_line2;
  const processSteps = (Array.isArray(s.services_process_steps) && s.services_process_steps.length > 0)
    ? s.services_process_steps
    : PROCESS_FALLBACK;

  return (
    <div data-testid="services-page" data-context={contextExistingBrand ? "existing-brand" : "default"}>
      {contextExistingBrand ? (
        // COMPACT CONTEXTUAL INTRO — replaces the generic marketing hero
        // when the customer arrived from the "I already have a complete
        // brand" gateway card. Purpose: fast onward choice, not another
        // marketing pitch. Kept intentionally short and text-only.
        <section
          data-testid="services-existing-brand-intro"
          className="pt-28 sm:pt-32 pb-8 sm:pb-10 bg-[#0F0F0F]"
        >
          <div className="max-w-[880px] mx-auto px-4 sm:px-6 lg:px-10 text-center">
            <Reveal>
              <div
                data-testid="services-existing-brand-eyebrow"
                className="ur-eyebrow mb-4"
              >
                Your Brand Is Ready
              </div>
              <h1
                data-testid="services-existing-brand-heading"
                className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mb-4"
              >
                What do you need <span className="text-[#FF6600]">next?</span>
              </h1>
              <p className="text-[#A3A3A3] max-w-xl mx-auto text-sm sm:text-base">
                Choose the type of creative work you need and we'll guide you from there.
              </p>
            </Reveal>
          </div>
        </section>
      ) : (
        // Default hero — unchanged from the pre-context implementation.
        <PageHero
          testId="services-hero"
          eyebrow={eyebrow}
          heading={heading1}
          accent={heading2}
          accentInline={!!s.services_accent_inline}
          showDot={!!s.services_show_dot}
          description={heroDesc}
          mobileDescription={heroDescMobile}
          ctaLabel={s.services_hero_cta_label}
          ctaLink={s.services_hero_cta_link}
        />
      )}

      {/* SERVICES FINDER — customer-facing shortcut above the service cards.
          Focus routes never auto-select a paid item (see useFocusRouting
          in PackageBuilder.jsx). Rendered above the browse grid so the
          simple discovery path is obvious. Compact desktop 4-column /
          mobile 2-column tile grid; uses the existing design system. */}
      <section
        id="services-finder"
        data-testid="services-finder"
        className="bg-[#0F0F0F] pt-8 sm:pt-12 pb-16 sm:pb-20 scroll-mt-24"
      >
        <div className="max-w-[1140px] xl:max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <h2
              data-testid="services-finder-heading"
              className="font-display text-[32px] sm:text-[38px] lg:text-[40px] font-black tracking-tight leading-[1.05]"
            >
              What do you need designed?
            </h2>
            <p className="mt-3 text-sm text-[#A3A3A3]">
              Choose what you're looking for — we'll take you straight there.
            </p>
          </div>

          <nav
            data-testid="services-group-filter"
            aria-label="Service categories"
            className="mt-6 flex flex-wrap justify-center gap-2"
          >
            {[{ key: "", label: "All Services" }, ...DISCOVERY_GROUPS].map((g) => {
              const active = activeGroup === g.key;
              return (
                <Link
                  key={g.key || "all"}
                  to={g.key ? `/services?group=${g.key}#services-finder` : "/services#services-finder"}
                  replace
                  data-testid={`services-group-${g.key || "all"}`}
                  aria-current={active ? "true" : undefined}
                  className={`px-3.5 py-1.5 rounded-full border text-xs font-semibold tracking-wide transition-colors ${
                    active
                      ? "bg-[#FF6600] border-[#FF6600] text-black"
                      : "border-[#262626] text-[#A3A3A3] hover:text-white hover:border-[#FF6600]"
                  }`}
                >
                  {g.label}
                </Link>
              );
            })}
          </nav>

          <div
            data-testid="services-finder-grid"
            // Mobile ↔ Homepage parity (Mar 2026): the outer container
            // is `flex flex-wrap justify-center` at ≤sm so a lone final
            // tile (13 tiles → 6 rows of 2 + 1 alone) is horizontally
            // centered on the page-center axis — same behavior as the
            // Homepage `home-services-finder`. From `sm:` up it
            // returns to the approved responsive grid (3/4/6 columns)
            // and at `xl:` the wrapper switches to the vertical column
            // layout. Each tile carries an explicit mobile width so
            // it renders with the same footprint as when it lived in
            // the 2-column grid; that width is reset from `sm:` up.
            className="mt-8 sm:mt-10 flex flex-wrap justify-center sm:grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 lg:gap-5 xl:grid-cols-none xl:flex xl:flex-col xl:gap-5"
          >
            {[
              finderTiles.slice(0, FINDER_ROW_SPLIT),
              finderTiles.slice(FINDER_ROW_SPLIT),
            ].map((row, rowIdx) => (
              <div
                key={`finder-row-${rowIdx + 1}`}
                data-testid={`services-finder-row-${rowIdx + 1}`}
                // `contents` at ≤lg makes this wrapper invisible to
                // layout so tiles flow inside the outer grid exactly
                // as before. At xl it becomes a real flex-wrap
                // container that horizontally centers its own row
                // on the same page-center axis.
                className="contents xl:flex xl:flex-wrap xl:justify-center xl:gap-5"
              >
                {row.map((tile) => (
                  <Link
                    key={tile.key}
                    to={tile.to}
                    data-testid={`finder-tile-${tile.key}`}
                    // MOBILE ↔ HOMEPAGE PARITY (Mar 2026):
                    // These tiles now mirror the Homepage service
                    // tile (`home-finder-tile-*`) exactly on mobile —
                    // same 1/1 aspect square, same icon-on-top
                    // (`justify-start`) composition, same padding
                    // rhythm (`pt-4 pb-2`), same icon slot (72×72)
                    // and title zone (min-h 2.4em, 12.5px).
                    // Explicit inline `aspectRatio: 1/1` guarantees
                    // squareness on Safari builds that ignore the
                    // `aspect-square` utility (same rationale as the
                    // Homepage). Desktop/tablet (sm+) styling is
                    // UNCHANGED — icon slot still 76px, `justify-
                    // center` composition, `py-4` padding, all the
                    // approved xl overrides preserved.
                    style={{ aspectRatio: "1 / 1" }}
                    className="group relative flex flex-col items-center justify-start sm:justify-center rounded-2xl border border-[#262626] bg-[#151515] hover:bg-[#191919] hover:border-[#FF6600]/60 aspect-square px-3 pt-4 pb-2 sm:py-4 transition-all focus:outline-none focus:ring-2 focus:ring-[#FF6600]/50 w-[calc((100%-0.75rem)/2)] sm:w-auto xl:w-[calc((100%-120px)/7)]"
                  >
                    {/* Icon slot — mobile matches Homepage exactly:
                        72×72 container, 62×62 PNG. sm+ keeps the
                        existing 76×76 / 70×70 Services sizing. */}
                    <span className="grid place-items-center h-[72px] w-[72px] sm:h-[76px] sm:w-[76px] shrink-0 transition-transform duration-200 group-hover:-translate-y-0.5">
                      <img
                        src={tile.icon}
                        alt=""
                        className="w-[62px] h-[62px] sm:w-[70px] sm:h-[70px] object-contain"
                        draggable="false"
                        loading="lazy"
                        decoding="async"
                        aria-hidden="true"
                      />
                    </span>
                    {/* Reserved title block — mobile matches Homepage
                        (mt-1.5, min-h 2.4em, 12.5px). sm+ keeps the
                        existing Services values (mt-3, min-h 2.7em). */}
                    <div className="mt-1.5 sm:mt-3 w-full flex items-start justify-center text-center min-h-[2.4em] sm:min-h-[2.7em]">
                      <span className="block text-[12.5px] sm:text-sm font-semibold text-white leading-tight break-words">
                        {tile.label}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ))}
          </div>

          {/* NOT-SURE CALLOUT — full-width bordered panel with a large
              question-mark icon, heading, supporting copy and dual
              actions (Project enquiry primary, WhatsApp secondary).
              This is now the single canonical "not sure?" help area
              on the Services page. The WhatsApp destination is the
              same one used previously by the retired consultation bar
              (settings.cta_whatsapp_url with a hard-coded fallback). */}
          <div
            data-testid="services-finder-not-sure"
            className="mt-8 sm:mt-10 rounded-2xl border border-[#FF6600]/40 bg-[#151515] px-4 sm:px-6 py-5 sm:py-6 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6"
          >
            <span className="shrink-0 grid place-items-center w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 border-[#FF6600] bg-[#0F0F0F]">
              <HelpCircle className="w-6 h-6 sm:w-7 sm:h-7 text-[#FF6600]" strokeWidth={1.75} aria-hidden="true" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-base sm:text-lg font-bold text-white leading-tight">
                Not sure what you need?
              </div>
              <div className="text-sm text-[#A3A3A3] mt-1 leading-snug">
                Tell us about your project and we'll guide you.
              </div>
            </div>
            <div
              data-testid="services-finder-not-sure-actions"
              className="w-full md:w-auto shrink-0 flex flex-col sm:flex-row md:flex-row gap-3"
            >
              <Link
                to="/contact"
                data-testid="finder-not-sure-cta"
                className="ur-btn-primary w-full sm:w-auto md:w-auto"
              >
                Tell Us About Your Project <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={s.cta_whatsapp_url}
                target="_blank"
                rel="noreferrer"
                data-testid="finder-not-sure-whatsapp"
                className="ur-btn-secondary w-full sm:w-auto md:w-auto"
              >
                <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
              </a>
            </div>
          </div>

          {/* Oct 2026 — the eight photographic service cards were retired:
              every "See Your Options" destination is already covered by the
              21 finder tiles above, and every "View Examples" button pointed
              at the generic /portfolio page. The CMS scope note and the
              existing-brand escape link are preserved verbatim below. */}
          {scopeNote && (
            <div
              data-testid="services-scope-note"
              className="mt-10 text-center text-xs text-[#A3A3A3] italic"
            >
              {scopeNote}
            </div>
          )}
          {contextExistingBrand && (
            <div className="mt-6 text-center">
              <Link
                to="/services"
                data-testid="services-view-all-link"
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-[#A3A3A3] hover:text-white transition-colors"
              >
                View All Services
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* PROCESS — restrained polish pass (Feb 2026):      {/* PROCESS — restrained polish pass (Feb 2026):
          - Connector arrows removed (numbered sequence 01→04 already
            communicates order).
          - Each step uses fixed vertical zones (icon → step number →
            title → description) so all four steps share the same
            icon-Y, number-Y, title-Y and description-start-Y even if
            individual titles wrap.
          - Layout stays open (no heavy cards). */}
      <section className="bg-[#0F0F0F] py-20 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-[1fr_2.4fr] gap-10 lg:gap-14 items-start">
          <div>
            <div data-testid="services-process-eyebrow" className="ur-eyebrow">{processEyebrow}</div>
            <h2 data-testid="services-process-heading" className="font-display mt-4 text-4xl sm:text-5xl font-black tracking-tight leading-[1.05]">
              {processH1}
              {s.services_process_accent_inline
                ? <span className="text-[#FF6600]">{processH2}</span>
                : (<>
                    <br />
                    <span className="text-[#FF6600]">{processH2}</span>
                  </>)
              }
            </h2>
            {!!s.services_process_show_dot && <BrandDot testId="services-process-dot" className="!justify-start !my-3" />}
          </div>
          <div
            data-testid="services-process-steps"
            // Mobile (Feb 2026): 2×2 grid with 24px col gap / 48px
            // row gap so the four steps fit two-up on phones without
            // stacking into a long vertical column. sm+ preserved
            // exactly (2-up already; row/col gaps unchanged from
            // gap-y-12 / gap-x-6). Desktop lg+ still 4-across.
            className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-6 lg:gap-x-8"
          >
            {processSteps.map((step, i) => {
              const Icon = Icons[step.icon] || Icons.ClipboardList;
              const n = String(i + 1).padStart(2, "0");
              return (
                <div
                  key={i}
                  data-testid={`process-step-${i}`}
                  className="flex flex-col"
                >
                  {/* Icon zone — standalone orange line glyph on the
                      dark panel (Feb 2026 refinement). No circle,
                      border, plate, or background. Sized ~40 px on
                      mobile and ~52 px at sm+ so the icon carries
                      real visual presence in the composition while
                      leaving room in the 2×2 mobile grid. */}
                  <div className="h-11 sm:h-14 flex items-start">
                    <Icon className="w-10 h-10 sm:w-[52px] sm:h-[52px] text-[#FF6600]" strokeWidth={1.6} aria-hidden="true" />
                  </div>
                  {/* Step number — leading-none so consistent typographic
                      baseline; fixed top margin so all four numbers align.
                      Slightly larger gap under the icon per Feb 2026
                      brief so the standalone glyph reads with premium
                      breathing room before the number. */}
                  <div className="mt-6 sm:mt-7 font-display text-2xl sm:text-3xl font-black text-[#FF6600] leading-none">
                    {n}
                  </div>
                  {/* Title — reserved min-height fits up to two lines
                      at leading-tight so a wrapped title never pushes
                      the description below its siblings. */}
                  <div className="mt-2.5 sm:mt-3 font-bold tracking-[0.15em] text-xs uppercase leading-tight min-h-[2.4em] flex items-start">
                    {step.title}
                  </div>
                  {/* Description — no max-width on mobile so it fills
                      the column naturally; sm+ keeps the 30ch cap. */}
                  <div className="mt-1 text-sm text-[#A3A3A3] leading-relaxed sm:max-w-[30ch]">
                    {step.sub}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
